import { Button, Card, Result, Spin, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { exchangeToken } from '@/api/auth'
import { ApiError } from '@/api/client'
import { env } from '@/config/env'
import { isDevAuthBypass } from '@/lib/devAuth'
import { useAuthStore } from '@/store/useAuthStore'
import { OAUTH_FROM_KEY, OAUTH_STATE_KEY } from '@/utils/storage'

const { Title, Paragraph } = Typography

/** StrictMode remount 共用：同一 code 只换一次。 */
let inFlightOAuthCode: string | null = null
/** 同一 token 只自动跳转一次，避免 StrictMode / 回调残留 code 时吞掉 from。 */
let redirectedForToken: string | null = null

function genState(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '')
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

/** 与 Hydra 白名单一致：始终是本页 origin，不带 query。 */
function getRedirectUri(): string {
  return `${location.origin}/login`
}

function safeInternalPath(raw: string | null): string {
  if (!raw) return '/apps'
  let decoded = raw
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    return '/apps'
  }
  if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.startsWith('/login')) {
    return '/apps'
  }
  return decoded
}

function peekPostLoginPath(urlFrom: string | null): string {
  const stored = sessionStorage.getItem(OAUTH_FROM_KEY)
  return safeInternalPath(urlFrom || stored)
}

function clearPostLoginPath() {
  sessionStorage.removeItem(OAUTH_FROM_KEY)
}

export default function LoginPage() {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const ready = useAuthStore((s) => s.ready)
  const token = useAuthStore((s) => s.token)
  const setSession = useAuthStore((s) => s.setSession)

  const [error, setError] = useState<string | null>(null)

  const oauthCode = params.get('code')
  const oauthState = params.get('state')
  const isOAuthCallback = Boolean(oauthCode && oauthState)
  const urlFrom = params.get('from')
  const bypass = isDevAuthBypass()

  useEffect(() => {
    if (bypass) {
      navigate('/apps', { replace: true })
      return
    }
    if (!ready || !token) return
    if (redirectedForToken === token) return
    redirectedForToken = token
    const dest = peekPostLoginPath(urlFrom)
    clearPostLoginPath()
    navigate(dest, { replace: true })
  }, [bypass, ready, token, navigate, urlFrom])

  useEffect(() => {
    if (bypass || !isOAuthCallback || !oauthCode || !oauthState) return
    if (inFlightOAuthCode === oauthCode) return
    inFlightOAuthCode = oauthCode

    const expected = sessionStorage.getItem(OAUTH_STATE_KEY)
    sessionStorage.removeItem(OAUTH_STATE_KEY)
    if (!expected || expected !== oauthState) {
      setError(t('login.stateMismatch'))
      inFlightOAuthCode = null
      return
    }

    void exchangeToken({
      code: oauthCode,
      state: oauthState,
      redirect_uri: getRedirectUri(),
    })
      .then((info) => {
        const jwt = info.token || info.access_token
        if (!jwt) {
          setError(t('login.failed'))
          return
        }
        // 只写会话；跳转交给上面 effect，避免与回调 URL 竞态、from 被吃掉
        setSession({ token: jwt, userId: info.user_id, name: info.name })
      })
      .catch((err: unknown) => {
        console.error('[login] exchangeToken failed:', err instanceof ApiError ? err.message : err)
        setError(t('login.failed'))
      })
      .finally(() => {
        inFlightOAuthCode = null
      })
  }, [bypass, isOAuthCallback, oauthCode, oauthState, setSession, t])

  const onLogin = () => {
    if (!env.authUrl) {
      setError(t('login.misconfigured'))
      return
    }
    redirectedForToken = null
    const state = genState()
    sessionStorage.setItem(OAUTH_STATE_KEY, state)
    sessionStorage.setItem(OAUTH_FROM_KEY, safeInternalPath(urlFrom))
    const url = new URL(env.authUrl)
    url.searchParams.set('response_type', 'code')
    url.searchParams.set('client_id', env.oauthClientId)
    url.searchParams.set('redirect_uri', getRedirectUri())
    url.searchParams.set('scope', env.oauthScope)
    url.searchParams.set('state', state)
    location.href = url.toString()
  }

  // 旁路 / 已登录跳转中 / 换码中 → Spin；失败才露登录卡
  if (bypass || token || (isOAuthCallback && !error)) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Spin size="large" tip={t('login.exchanging')} />
      </div>
    )
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-[#f5f5f5] p-6">
      <Card style={{ width: 420 }}>
        <Title level={3} style={{ marginTop: 0 }}>
          {t('login.title')}
        </Title>
        <Paragraph type="secondary">{t('login.subtitle')}</Paragraph>
        {error ? (
          <Result
            status="error"
            title={t('login.failed')}
            subTitle={error === t('login.failed') ? undefined : error}
          />
        ) : null}
        <Button type="primary" block onClick={onLogin}>
          {t('login.signIn')}
        </Button>
      </Card>
    </div>
  )
}
